import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { MailmanagerService } from "../../../api/services/mailmanager.service";
import {
  AlertController,
  IonModal,
  ModalController,
  Platform,
} from "@ionic/angular";
import { MailmanagerAction } from "../../enums/global-enums";
import { MailmanagerMailboxEmailAssignRequest } from "../../../api/models/mailmanager-mailbox-email-assign-request";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { MailmanagerEmailResponse } from "../../../api/models/mailmanager-email-response";
import { MailmanagerMailboxEmailContentRequest } from "../../../api/models/mailmanager-mailbox-email-content-request";
import { MailmanagerMailboxEmailContentResponse } from "../../../api/models/mailmanager-mailbox-email-content-response";
import { DownloadService } from "../../services/download.service";

@Component({
  selector: "app-email-content",
  templateUrl: "./email-content.component.html",
  styleUrls: ["./email-content.component.scss"],
  standalone: false,
})
export class EmailContentComponent implements OnInit {
  @ViewChild("aktsucheModal") aktsucheModal: IonModal;
  @ViewChild("aktsucheModal", { read: ElementRef })
  aktsucheModalElementRef: ElementRef;

  @Input({ required: true }) emailHeader: MailmanagerEmailResponse;
  @Input({ required: false })
  emailContent: MailmanagerMailboxEmailContentResponse;
  @Input({ required: true }) userSb: string;
  @Input({ required: false }) mailboxFolderId: string;
  @Input({ required: false }) mailboxEmailId: string;
  @Input({ required: false }) emlFileBlob: Blob;
  @Input({ required: false }) emlFileName: string;
  @Input({ required: false }) isProcessing: boolean;

  width: number = 0;
  minWidth: number = 0;
  height: number = 0;
  minHeight: number = 0;
  resizing: boolean = false;
  moving: boolean = false;
  lastMouseX: number = 0;
  lastMouseY: number = 0;
  offsetX: number = 0;
  offsetY: number = 0;
  positionX = 100;
  positionY = 100;

  constructor(
    private mailmanagerService: MailmanagerService,
    private alertController: AlertController,
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
    private platform: Platform,
    protected downloadService: DownloadService,
    protected renderer: Renderer2,
  ) {}

  ngOnInit() {
    if (this.mailboxFolderId && this.mailboxEmailId) {
      this.getMailmanagerEmailContent();
    }
  }

  openAktsucheModal() {
    this.aktsucheModal.present();
    const modalElement = this.aktsucheModalElementRef?.nativeElement;
    if (
      modalElement &&
      this.platform.width() >= 1320 &&
      this.platform.height() >= 800
    ) {
      const computedStyle = getComputedStyle(modalElement);
      this.width = parseFloat(computedStyle.getPropertyValue("--width").trim());
      this.height = parseFloat(
        computedStyle.getPropertyValue("--height").trim(),
      );
      this.minWidth = parseFloat(
        computedStyle.getPropertyValue("--min-width").trim(),
      );
      this.minHeight = parseFloat(
        computedStyle.getPropertyValue("--min-height").trim(),
      );

      const boundingRect = modalElement.getBoundingClientRect();
      this.positionX = boundingRect.left;
      this.positionY = boundingRect.top;
      this.updateModalSizeAndPosition();
    }
  }

  downloadEmlBlob() {
    this.downloadService.createLinkAndDownloadFile(
      this.emlFileBlob,
      this.emlFileName,
    );
  }

  cancelButtonEmail() {
    this.modalController.dismiss(null, "cancel");
  }

  private getMailboxEmailContentRequestParams(
    folderId: string,
    emailId: string,
  ): MailmanagerMailboxEmailContentRequest {
    return {
      token: this.oidcSecurityService.getToken(),
      user_id: this.userSb,
      folder_id: folderId,
      email_id: emailId,
    };
  }

  private getMailboxEmailAssignRequestParams(
    folderId: string,
    emailId: string,
    action: MailmanagerAction,
    recordId: number = null,
    documentType: number = null,
    subject: string = null,
  ): MailmanagerMailboxEmailAssignRequest {
    return {
      token: this.oidcSecurityService.getToken(),
      user_id: this.userSb,
      folder_id: folderId,
      email_id: emailId,
      action: action,
      record_id: recordId,
      document_type: documentType,
      subject: subject,
    };
  }

  async deleteEmailButton() {
    const alert = await this.alertController.create({
      header: "Diese E-Mail löschen?",
      message: "Gelöschte Emails werden in den Papierkorb verschoben.",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.mailmanagerService
              .mailmanagerMailboxEmailAssignCreate(
                this.getMailboxEmailAssignRequestParams(
                  this.mailboxFolderId,
                  this.mailboxEmailId,
                  MailmanagerAction.TRASH,
                ),
              )
              .subscribe((response) => {
                this.modalController.dismiss(null, "confirm");
              });
          },
        },
      ],
    });
    document.body.appendChild(alert);
    await alert.present();
  }

  async zuordnenOhneAktbezugButton() {
    const alert = await this.alertController.create({
      header: "Zuordnen ohne Aktbezug",
      message: "Diese E-Mail ohne Aktbezug zuordnen?",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-secondary",
          text: "Zuordnen",
          handler: () => {
            this.mailmanagerService
              .mailmanagerMailboxEmailAssignCreate(
                this.getMailboxEmailAssignRequestParams(
                  this.mailboxFolderId,
                  this.mailboxEmailId,
                  MailmanagerAction.PRIVATE,
                ),
              )
              .subscribe((response) => {
                this.modalController.dismiss(null, "confirm");
              });
          },
        },
      ],
    });
    document.body.appendChild(alert);
    await alert.present();
  }

  getMailmanagerEmailContent() {
    if (this.emailContent) {
      return;
    }
    this.mailmanagerService
      .mailmanagerMailboxEmailContentRetrieveCreate(
        this.getMailboxEmailContentRequestParams(
          this.mailboxFolderId,
          this.mailboxEmailId,
        ),
      )
      .subscribe((result: MailmanagerMailboxEmailContentResponse) => {
        this.emailContent = {
          message: result.message,
          attachments: result.attachments,
        };
      });
  }

  cancelButtonAktsuche() {
    this.aktsucheModal.dismiss(null, "cancel");
  }

  afterEmailZuordnen = (zuordnenData: any) => {
    this.aktsucheModal.dismiss();
    this.mailmanagerService
      .mailmanagerMailboxEmailAssignCreate(
        this.getMailboxEmailAssignRequestParams(
          this.mailboxFolderId,
          this.mailboxEmailId,
          MailmanagerAction.ASSIGN,
          zuordnenData.aktId,
          zuordnenData.dokumentArt,
        ),
      )
      .subscribe((response) => {
        this.modalController.dismiss(null, "confirm");
      });
  };

  startResizing(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  startMoving(event: MouseEvent): void {
    this.moving = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
    event.preventDefault();
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    const deltaX = event.clientX - this.lastMouseX;
    const deltaY = event.clientY - this.lastMouseY;

    if (this.resizing) {
      this.width += deltaX;
      if (this.width < this.minWidth) {
        this.width = this.minWidth;
      }
      this.height += deltaY;
      if (this.height < this.minHeight) {
        this.height = this.minHeight;
      }
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.updateModalSizeAndPosition();
    } else if (this.moving) {
      this.positionX += deltaX;
      this.positionY += deltaY;

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.updateModalSizeAndPosition();
    }
  }

  @HostListener("document:mouseup")
  onMouseUp(): void {
    this.resizing = false;
    this.moving = false;
  }

  updateModalSizeAndPosition(): void {
    if (this.platform.width() < 1320 || this.platform.width() < 800) {
      return;
    }
    const modalElement = this.aktsucheModalElementRef?.nativeElement;
    this.renderer.setStyle(modalElement, "--width", `${this.width}px`, 2);
    this.renderer.setStyle(modalElement, "--height", `${this.height}px`, 2);
    this.renderer.setStyle(
      modalElement,
      "transform",
      `translate(${this.positionX}px, ${this.positionY}px)`,
    );
  }
}
